<script setup>
import { produktnavn } from '@/Composables/produktnavn';
import { Link } from '@inertiajs/vue3';
import { computed } from 'vue';
import CompareButton from '@/Components/Compare/CompareButton.vue';

const props = defineProps({
    product: {
        type: Object,
        required: true,
    },
    activeFilters: {
        type: Object,
        default: () => ({}),
    },
    specColumns: {
        type: Array,
        default: () => [],
    },
    // Enhedens navn, fx "TB". Sat af kategorien; null når kategorien ikke har
    // en mængde at dividere prisen med.
    unitPriceLabel: {
        type: String,
        default: null,
    },
});

// Sammenligningslisten gemmes på slug, ikke på id, fordi /sammenlign slår op
// på slug. route_to_productdetail er en fuld URL, så halen skal skæres af.
const compareSlug = computed(() => {
    const url = props.product.route_to_productdetail || '';
    const del = url.split('/produkt/');
    return del.length > 1 ? del[1].replace(/[/?#].*$/, '') : (props.product.slug || '');
});

const thumbnailUrl = computed(() => {
    if (props.product.images && props.product.images.length > 0) {
        return `/images/${props.product.images[0].id}`;
    }
    return null;
});

const formattedPrice = computed(() => {
    const value = Number(props.product.prices?.lowest_price_including_shipping);
    if (isNaN(value)) return '';
    return new Intl.NumberFormat('da-DK', {
        style: 'currency',
        currency: 'DKK',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(value);
});

const retailerCount = computed(() => props.product.prices?.total_retailers ?? 0);

const stockStatus = computed(() => {
    const lowestHasStock = props.product.prices?.lowest_price_has_in_stock;
    const othersHaveStock = props.product.prices?.others_has_in_stock;
    if (lowestHasStock || othersHaveStock) {
        return { inStock: true, label: 'På lager' };
    }
    return { inStock: false, label: 'Ikke på lager' };
});

// Build a lookup: filter_id -> display value from product attributes
const attrByFilterId = computed(() => {
    const map = {};
    const attrs = props.product.attributes;
    if (!Array.isArray(attrs)) return map;
    for (const attr of attrs) {
        const fid = attr.filter_id;
        if (fid != null && !map[fid]) {
            map[fid] = attr.value_human_displayable || attr.value || '';
        }
    }
    return map;
});

// Nogle attributter lagres som rå bytes (kapacitet/VRAM) eller som array
// (fx skærmopløsning) i indekset. Formatér dem pænt til kolonnevisning.
const MiB = 1048576, GiB = 1073741824, TiB = 1099511627776;
function formatSpec(val) {
    if (Array.isArray(val)) return val.filter(v => v !== null && v !== '').join(' / ');
    if (val === null || val === undefined || val === '') return '';
    const s = String(val);
    if (/^\d+$/.test(s)) {
        const n = Number(s);
        if (n >= TiB) { const v = n / TiB; return (Number.isInteger(v) ? v : v.toFixed(1)) + ' TB'; }
        if (n >= GiB) return Math.round(n / GiB) + ' GB';
        if (n >= MiB) return Math.round(n / MiB) + ' MB';
    }
    return s;
}

// Resolve each spec column to a value for this product
const specValues = computed(() => {
    return props.specColumns.map(col => formatSpec(attrByFilterId.value[col.filter_id]));
});

/*
 * Pris pr. enhed, fx "412 kr./TB".
 *
 * "EDB-priser havde også flere søgefunktioner end Pricerunner, f.eks. pris/MB
 * (TB osv)." Det er det tal der gør "den største SSD til de mindste penge"
 * til et opslag i stedet for hovedregning, og hverken PriceRunner eller
 * Prisjagt viser det. Tallet regnes ved indeksering, ikke her, så det også
 * kan sorteres på.
 */
const enhedspris = computed(() => {
    if (!props.unitPriceLabel) return null;

    const v = Number(props.product.unit_price);
    if (!Number.isFinite(v) || v <= 0) return null;

    const decimaler = v < 10 ? 2 : 0;
    return `${new Intl.NumberFormat('da-DK', {
        minimumFractionDigits: decimaler,
        maximumFractionDigits: decimaler,
    }).format(v)} kr./${props.unitPriceLabel}`;
});

const hasSpecs = computed(() => props.specColumns.length > 0);

// Fallback: show attribute_texts when no specColumns configured
const fallbackSpecs = computed(() => {
    if (hasSpecs.value) return [];
    const raw = Array.isArray(props.product.attribute_texts) ? props.product.attribute_texts : [];
    return raw.slice(0, 4);
});
</script>

<template>
    <!--
        Rækken er en wrapper med selve linket indeni, ikke ét stort <a>.
        Sammenlign-knappen skal kunne klikkes uden at man ryger ind på
        produktet, og en <button> inde i et <a> er ugyldig HTML.
    -->
    <div class="plr">
    <Link :href="product.route_to_productdetail" class="plr__link">
        <!-- Image -->
        <div class="plr__img-wrap">
            <picture v-if="thumbnailUrl">
                <source :srcset="`${thumbnailUrl}?w=160&fmt=webp`" type="image/webp" />
                <!--
                    width og height afsætter pladsen FØR billedet er hentet.
                    Uden dem hopper listen når billederne lander, og
                    layoutforskydning er et rangeringssignal.
                -->
                <img
                    :src="`${thumbnailUrl}?w=160`"
                    :alt="produktnavn(product)"
                    class="plr__img"
                    width="80"
                    height="80"
                    loading="lazy"
                    decoding="async"
                />
            </picture>
            <div v-else class="plr__img-placeholder">
                <i class="bi bi-image"></i>
            </div>
        </div>

        <!-- Name -->
        <div class="plr__name-col">
            <span class="plr__name">{{ produktnavn(product) }}</span>
            <!-- Farvevarianter er samlet til én række. Uden det her stod
                 "Google Pixel 11" fire gange i træk i Mobiltelefon. -->
            <span v-if="product.variant_count > 1" class="plr__varianter">
                {{ product.variant_count }} varianter
            </span>
            <!-- Fallback inline specs when no columns configured -->
            <span v-if="fallbackSpecs.length" class="plr__fallback-specs">
                {{ fallbackSpecs.join(' · ') }}
            </span>
        </div>

        <!-- Spec columns (aligned across all rows) -->
        <div v-if="hasSpecs" class="plr__specs">
            <span
                v-for="(val, i) in specValues"
                :key="i"
                class="plr__spec"
                :title="specColumns[i]?.name"
            >{{ val || '–' }}</span>
        </div>

        <!-- Price block -->
        <div class="plr__price-block">
            <span class="plr__price">{{ formattedPrice }}</span>
            <span v-if="enhedspris" class="plr__unit-price">{{ enhedspris }}</span>
            <span class="plr__meta">
                <span class="plr__retailers">{{ retailerCount }} {{ retailerCount === 1 ? 'butik' : 'butikker' }}</span>
                <span :class="['plr__stock', stockStatus.inStock ? 'plr__stock--in' : 'plr__stock--out']">
                    <i :class="stockStatus.inStock ? 'bi bi-check-circle-fill' : 'bi bi-x-circle'"></i>
                    {{ stockStatus.label }}
                </span>
            </span>
        </div>

        <!-- CTA -->
        <div class="plr__cta">
            <span class="plr__cta-btn">Gå til produkt</span>
        </div>
    </Link>

        <!-- Sammenligning. Lå indtil nu kun som en ubrugt komponent, så der
             var ingen vej ind i sammenligningsfunktionen overhovedet. -->
        <div class="plr__compare">
            <CompareButton
                :productSlug="compareSlug"
                :productName="produktnavn(product)"
                :productImage="thumbnailUrl ? `${thumbnailUrl}?w=96` : ''"
            />
        </div>
    </div>
</template>

<style scoped>
.plr {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    border-bottom: 1px solid var(--pc-border);
    color: var(--pc-text-primary);
    transition: background var(--pc-transition);
}

.plr:hover {
    background: var(--pc-bg-hover);
}

.plr__link {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1 1 auto;
    min-width: 0;
    text-decoration: none;
    color: inherit;
}

.plr__link:hover {
    text-decoration: none;
    color: inherit;
}

.plr__compare {
    flex: 0 0 auto;
}

/* ── Image ── */
.plr__img-wrap {
    flex: 0 0 64px;
    width: 64px;
    height: 64px;
    border-radius: var(--pc-radius);
    overflow: hidden;
    background: #f4f4f4;
    display: flex;
    align-items: center;
    justify-content: center;
}

.plr__img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
}

.plr__img-placeholder {
    color: var(--pc-text-muted);
    font-size: 1.25rem;
}

/* ── Name ── */
.plr__name-col {
    flex: 1 1 0;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 3px;
}

.plr__name {
    font-weight: 600;
    font-size: 0.85rem;
    line-height: 1.3;
    color: var(--pc-text-primary);
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.plr__fallback-specs {
    font-size: var(--pc-text-xs);
    color: var(--pc-text-secondary);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

/* ── Specs (aligned columns) ── */
.plr__specs {
    flex: 1 1 0;
    min-width: 0;
    display: flex;
    gap: 0;
}

.plr__spec {
    flex: 1 1 0;
    min-width: 0;
    font-size: var(--pc-text-xs);
    color: var(--pc-text-secondary);
    padding: 0 8px;
    border-left: 1px solid var(--pc-border);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.plr__spec:first-child {
    border-left: none;
    padding-left: 0;
}

/* ── Price block ── */
.plr__price-block {
    flex: 0 0 150px;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 3px;
    text-align: right;
}

.plr__price {
    font-weight: 700;
    font-size: 0.95rem;
    color: var(--pc-price);
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
}

.plr__meta {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 1px;
}

.plr__retailers {
    font-size: var(--pc-text-xs);
    color: var(--pc-text-muted);
}

.plr__stock {
    font-size: var(--pc-text-xs);
    font-weight: 500;
    display: inline-flex;
    align-items: center;
    gap: 3px;
}

.plr__stock--in {
    color: var(--pc-success);
}

.plr__stock--out {
    color: var(--pc-text-muted);
}

.plr__varianter {
    font-size: var(--pc-text-xs);
    color: var(--pc-text-muted);
    border: 1px solid var(--pc-border);
    border-radius: 999px;
    padding: 0 8px;
    align-self: flex-start;
}

.plr__unit-price {
    font-size: var(--pc-text-xs);
    color: var(--pc-accent);
    font-weight: 600;
    white-space: nowrap;
}

/* ── CTA ── */
.plr__cta {
    flex: 0 0 auto;
}

.plr__cta-btn {
    display: inline-block;
    padding: 7px 16px;
    border: 1px solid var(--pc-accent);
    border-radius: var(--pc-radius);
    color: var(--pc-accent);
    font-size: var(--pc-text-sm);
    font-weight: 600;
    white-space: nowrap;
    transition: all var(--pc-transition);
}

.plr__link:hover .plr__cta-btn,
.plr:hover .plr__cta-btn {
    background: var(--pc-accent);
    color: var(--pc-bg-primary);
    box-shadow: 0 0 12px var(--pc-accent-glow);
}

/* ── Responsive ── */
@media (max-width: 1199px) {
    .plr__cta {
        display: none;
    }
}

@media (max-width: 991px) {
    .plr__specs {
        display: none;
    }

    .plr__price-block {
        flex: 0 0 auto;
    }
}

@media (max-width: 767px) {
    .plr {
        flex-wrap: wrap;
        gap: 10px;
        padding: 12px 14px;
    }

    .plr__img-wrap {
        flex: 0 0 52px;
        width: 52px;
        height: 52px;
    }

    .plr__name-col {
        flex: 1 1 0;
        min-width: 100px;
    }

    .plr__name {
        font-size: 0.8rem;
    }

    .plr__price-block {
        flex: 0 0 auto;
        align-items: flex-end;
    }
}

@media (max-width: 480px) {
    .plr__price-block {
        flex: 1 1 100%;
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
        border-top: 1px solid var(--pc-border);
        padding-top: 8px;
    }

    .plr__meta {
        flex-direction: row;
        gap: 10px;
    }
}
</style>
